/**
 * src/lib/voice/useVoiceBargeIn.js — TICKET-103 barge-in v2
 *
 * Speaking-mode VAD extracted from useVoiceConversation. While state is
 * 'speaking' AND active=true, a short-lived mic stream + VAD listens for
 * the user's voice; on detection, onBargeIn() fires (parent stops TTS
 * and opens a new recording turn).
 *
 * TICKET-103 — Rolling noise floor replaces one-shot calibration (TICKET-101).
 *
 * Problem proven by CEO-device telemetry (8 tests, 6/8 and 2/8 failure modes):
 *   A) 700ms window sampled SILENCE (audio not yet started) → floor ≈ 0.02
 *      → guard useless → any ambient spike became a barge-in.
 *   B) 700ms window sampled FULL TTS volume → threshold 0.20-0.25
 *      → no human voice can cross → assistant uninterruptible.
 *
 * Fix: continuous EMA floor (rollingAlpha=0.08) gated to sourceActive signal.
 *   - Floor only adapts while an AudioBufferSource is genuinely playing.
 *   - Between sentence chunks (silence) the floor is frozen.
 *   - maxThreshold=0.25 caps the effective threshold so a loud speaker can
 *     never make barge-in impossible.
 *   - onFloorUpdate emits spine telemetry (numbers only, no PII) so the next
 *     CEO test session can diagnose floor + threshold from app_error_logs.
 *
 * sourceActive signal: the hook accepts a `getSourceActive` prop (a function
 * returning bool) that is polled each time the TTS source starts/ends.
 * useVoiceConversation wires this to useStreamingTTS's `currentSourceRef`.
 *
 * Hold-to-talk composition: this hook mounts independently of vadPaused.
 * vadPaused in useVoiceConversation suspends useVoiceAutoMode (the idle-listen
 * VAD). Barge-in is a separate layer — it is only active during state='speaking'
 * and only fires when sourceActive=true (actual TTS playing). During hold-to-talk,
 * TTS is typically not playing, so sourceActive=false → floor stays frozen →
 * threshold stays at the last-known value → no barge-in spuriously fires.
 * If hold-to-talk IS used while TTS plays (edge case), the same rolling floor
 * correctly gates the detection.
 *
 * AEC loopback (research — NOT implemented here): a WebAudio loopback node that
 * subtracts the exact speaker output from the mic signal would provide a true
 * acoustic echo cancellation fallback for Safari (which doesn't cancel WebAudio
 * output via OS-level AEC). Research direction: capture speaker via
 * AudioContext.destination → MediaStreamDestinationNode → getUserMedia reference
 * stream → subtract from mic stream in AudioWorkletProcessor. Left for TICKET-108.
 *
 * Active flag semantics:
 *   - state='speaking' (normal reply) + active=true  → barge-in enabled
 *   - state='awaiting_confirmation'   + active=false → barge-in DISABLED
 *     so the proposal TTS ("Create task X — confirm?") finishes before
 *     the user's reply starts recording (76-02 AC-8).
 *
 * Test seams: createVAD, navigator, getSourceActive.
 */

import { useEffect, useRef } from 'react';
import { createVAD as defaultCreateVAD } from './voiceActivityDetector';
import { logVoiceEvent } from './voiceLogger';
import { registerMic, releaseMic } from './micRegistry';

/**
 * TICKET-103 rolling floor tuning constants.
 * Exported for observability + bench reuse (e.g. vad-adaptive-calibration.test.js).
 * calibrationMs intentionally absent — rolling mode replaces one-shot.
 */
export const BARGE_IN_VAD_TUNING = {
  holdMs: 250,
  rollingAlpha: 0.08,
  thresholdMultiplier: 2.5,
  maxThreshold: 0.25,
  // absoluteMin threshold — floor × multiplier is always at least this
  threshold: 0.02,
};

/**
 * Throttle helper — returns a function that calls `fn` at most once per
 * `intervalMs`. Used to avoid flooding app_error_logs with per-frame events.
 */
function makeThrottle(fn, intervalMs) {
  let lastCall = 0;
  return (...args) => {
    const now = Date.now();
    if (now - lastCall >= intervalMs) {
      lastCall = now;
      fn(...args);
    }
  };
}

export default function useVoiceBargeIn({
  state,
  onBargeIn,
  active = true,
  createVAD = defaultCreateVAD,
  navigator: navOverride,
  // TICKET-103: function returning bool — true when an AudioBufferSource is
  // actively playing. Wired by useVoiceConversation from useStreamingTTS's
  // currentSourceRef. Defaults to always-false (safe: floor stays frozen
  // until the caller provides the signal — no spurious barge-ins on mount).
  getSourceActive,
  // TICKET-623 — function returning pcmPlayer's recent {rms,t}[] envelope
  // (wired from useStreamingTTS's getReferenceEnvelope). Undefined = the
  // echo guard's kill switch: the VAD's echoGate never receives a reference
  // and fails open exactly like pre-TICKET-623 behavior. Optional so the
  // legacy buffered TTS path (no pcmPlayer, e.g. Gemini fallback) works
  // unchanged.
  getReferenceEnvelope,
} = {}) {
  // Ref so the rAF loop always reads the latest value without re-creating the VAD.
  const getSourceActiveRef = useRef(getSourceActive);
  getSourceActiveRef.current = getSourceActive;
  const getReferenceEnvelopeRef = useRef(getReferenceEnvelope);
  getReferenceEnvelopeRef.current = getReferenceEnvelope;

  useEffect(() => {
    if (state !== 'speaking') return undefined;
    if (!active) return undefined;
    const nav = navOverride || (typeof navigator !== 'undefined' ? navigator : null);
    if (!nav?.mediaDevices?.getUserMedia) return undefined;

    let cancelled = false;
    let localStream = null;
    let localVad = null;
    // TICKET-103: poll interval for sourceActive signal injection.
    // We push sourceActive to the VAD on each rAF tick via the VAD's own tick,
    // but the VAD needs the signal synchronously from the parent. We use a
    // small setInterval to keep the VAD's internal flag current between frames.
    let sourceActiveIntervalId = null;

    // Throttled floor update logger — ~1 log per second max to avoid spam.
    const logFloorUpdate = makeThrottle(({ floor, threshold: thr, sourceActive: sa }) => {
      logVoiceEvent('barge_in_guard', 'ok',
        `rolling floor=${Number(floor).toFixed(4)} threshold=${Number(thr).toFixed(4)} sourceActive=${sa}`,
        {
          floor: parseFloat(floor.toFixed(4)),
          threshold: parseFloat(thr.toFixed(4)),
          source_active: sa,
          tuning: 'rolling_v2',
        });
    }, 1000);

    (async () => {
      try {
        // 2026-04-20 (session 4) — autoGainControl OFF (see useVoiceAutoMode
        // for the full rationale). Barge-in VAD must detect a clear speech
        // edge over ambient, and AGC boosts ambient into false-positive range.
        const stream = await nav.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: false,
          },
        });
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
        localStream = stream;
        // 2026-04-20 (session 3) — register with micRegistry so tab-hide /
        // stopAllMics / endSession sweeps this barge-in stream.
        registerMic(stream, null, 'useVoiceBargeIn:speaking_vad');

        localVad = createVAD({
          stream,
          ...BARGE_IN_VAD_TUNING,
          onFloorUpdate: logFloorUpdate,
          onSpeechStart: () => {
            logVoiceEvent('barge_in', 'ok', 'User voice detected during TTS — interrupting', {
              tuning: 'rolling_v2',
            });
            try { onBargeIn?.(); } catch (err) {
              console.warn('[useVoiceBargeIn] onBargeIn failed:', err?.message);
            }
          },
          // TICKET-623 — this is the telemetry that was missing before: a
          // suppressed transition used to be silent (stop() emits nothing).
          // Now every echo-classified crossing leaves a trace, so "is the
          // guard actually firing on real devices" is answerable from
          // app_error_logs instead of inferred from absence.
          onEchoSuppressed: (verdict) => {
            logVoiceEvent('barge_in_echo_suppressed', 'ok',
              `Speech-like frame classified as TTS echo, not promoted to barge-in (residualRms=${verdict.residualRms?.toFixed(4)})`,
              {
                residual_rms: verdict.residualRms,
                correlation: verdict.correlation,
                lag_ms: verdict.lagMs,
              });
          },
        });

        // TICKET-103: push sourceActive signal to the VAD every 16ms so the
        // rolling floor tracks actual playback state. setSourceActive is a
        // no-op when rolling mode is not active (createVAD falls back to
        // absolute-threshold mode); guard against mocked VADs that don't expose it.
        if (typeof localVad.setSourceActive === 'function') {
          sourceActiveIntervalId = setInterval(() => {
            if (!localVad || cancelled) return;
            const isActive = typeof getSourceActiveRef.current === 'function'
              ? Boolean(getSourceActiveRef.current())
              : false;
            localVad.setSourceActive(isActive);
            // TICKET-623 — same poll cadence feeds the echo guard's known
            // reference. getReferenceEnvelopeRef being unset is the kill
            // switch: pushReferenceEnvelope never gets called, echoGate
            // stays at reason:'no_reference', behavior is unchanged from
            // before this ticket.
            if (typeof localVad.pushReferenceEnvelope === 'function'
              && typeof getReferenceEnvelopeRef.current === 'function') {
              const entries = getReferenceEnvelopeRef.current();
              if (entries && entries.length) localVad.pushReferenceEnvelope(entries);
            }
          }, 16);
        }

      } catch (err) {
        logVoiceEvent('barge_in_init', 'error',
          `VAD init failed: ${err?.message || err}`,
          { error: String(err?.message || err).slice(0, 200) });
        console.warn('[useVoiceBargeIn] VAD init failed:', err?.message || err);
      }
    })();

    return () => {
      cancelled = true;
      if (sourceActiveIntervalId != null) clearInterval(sourceActiveIntervalId);
      if (localVad) { try { localVad.stop(); } catch { /* noop */ } }
      if (localStream) {
        try { releaseMic(localStream); } catch { /* noop */ }
      }
    };
  }, [state, active, onBargeIn, createVAD, navOverride]);
  // Note: getSourceActive intentionally NOT in the dep array — it's captured via
  // getSourceActiveRef so the VAD isn't torn down and remounted on every render.
}
